import {
  useNavigate as useRouterNavigate,
  useSearchParams as useRouterSearchParams,
  Navigate as RouterNavigate,
  NavigateOptions,
} from 'react-router-dom';
import { useSite, useDashboardSitesSelected } from './store';

export interface INavigateProps {
  pathname: string
  addSite?: boolean
  siteId?: string | null
  selected?: string | null
  search?: string
}

export interface IUseSiteHandler {
  addSite?: boolean
  siteId?: string | null
  pathname: string
}

const deserializeSearchParams = (serializedParams) => {
  const { params, ...searchParams } = Object.fromEntries(serializedParams);
  let parsedParams = {};
  if (params) {
    try {
      parsedParams = JSON.parse(atob(params));
    } catch (err) {
      console.log(err);
    }
  }

  return new URLSearchParams(
    Object.entries({ ...searchParams, ...parsedParams }),
  );
};

const serializeSearchParams = (deserializedParams) => {
  const searchParams = Object.fromEntries(deserializedParams);

  let params = '';
  if (Object.keys(searchParams).length > 0) {
    try {
      params = btoa(JSON.stringify(searchParams));
    } catch (err) {
      console.log(err);
    }
  }

  if (params) {
    return new URLSearchParams(`params=${params}`);
  } else {
    return new URLSearchParams('');
  }
};

export const useSearchParams = (): [URLSearchParams, (value: URLSearchParams) => void] => {
  const [params] = useRouterSearchParams();
  const searchParams = deserializeSearchParams(params);

  // UPDATE 13/11/2023: change condition to diable hook, typescript compliant
  // const setSearchParams = (newSearchParams) =>
  //   true === false && setParams(serializeSearchParams(newSearchParams));
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const setSearchParams = (_newSearchParams) => null

  return [searchParams, setSearchParams];
};

const useSiteHandler = (): (props: IUseSiteHandler) => string | undefined => {
  const [siteId, setSite] = useSite();
  return ({ addSite = true, siteId: newSiteId }) => {
    const currentId = newSiteId || siteId;
    if (addSite && currentId !== siteId) {
      setSite(currentId);
    } else if (!addSite) {
      setSite(undefined);
    }
    return currentId;
  };
};

const useSelectedHandler = () => {
  const [selected, setSelected] = useDashboardSitesSelected();
  return ({ selected: newSelected }) => {
    if (newSelected) {
      setSelected(newSelected);
      return newSelected;
    }
    return selected ? selected.slice(0, 100) : null
  };
};

const useSearchHandler = () => {
  return ({ addSite, siteId, selected, search = '' }) => {
    const searchParams = new URLSearchParams(search);
    if (addSite && siteId) {
      searchParams.set('siteId', String(siteId));
    }
    if (selected && selected.length) {
      searchParams.set(
        'selected',
        selected.map(({ sf_id }) => sf_id).join(','),
      );
    }
    return serializeSearchParams(searchParams).toString();
  };
};

export const useNavigate = () => {
  const navigate = useRouterNavigate();
  const siteHandler = useSiteHandler();
  const selectedHandler = useSelectedHandler();
  const searchHandler = useSearchHandler();
  return (
    {
      pathname,
      addSite = true,
      siteId: newSiteId = null,
      selected: newSelected = null,
      search: searchParams = '',
    }: INavigateProps,
    options?: NavigateOptions,
  ) => {
    const currentSiteId = siteHandler({ pathname, addSite, siteId: newSiteId });
    const currentSelected = selectedHandler({ selected: newSelected })
    const search = searchHandler({
      addSite,
      siteId: currentSiteId,
      selected: currentSelected,
      search: searchParams,
    })
    if (search) {
      navigate({ pathname, search: `?${search}` }, options);
    } else {
      navigate({ pathname }, options);
    }
  };
};

export const Navigate = ({
  pathname,
  addSite = true,
  siteId: newSiteId,
  selected: newSelected,
  search: searchParams,
}: INavigateProps) => {
  const siteHandler = useSiteHandler();
  const selectedHandler = useSelectedHandler();
  const searchHandler = useSearchHandler();

  const currentId = siteHandler({ pathname, addSite, siteId: newSiteId });
  const currentSelected = selectedHandler({ selected: newSelected });
  const search = searchHandler({
    addSite,
    siteId: currentId,
    selected: currentSelected,
    search: searchParams,
  });
  if (search) {
    return <RouterNavigate to={{ pathname, search: `?${search}` }} />;
  } else {
    return <RouterNavigate to={{ pathname }} />;
  }
}
